import React, { useState } from 'react';
import classNames from 'classnames';
import { DataRoomMember } from 'router/subrouters/DataRoom/models/DataRoomMember';
import { Button } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import PauseIcon from 'icons/Pause.icon';
import useUndoActions from 'hooks/useUndoActions';
import UndoButton from 'components/UndoButton';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import useCallbackAsync from 'hooks/useCallbackAsync';
import { dateFormat, isDateFromLastDays } from 'utils';

import './UserWithAccess.styles.scss';

type UserWithAccessProps = {
  className?: string;
  nodeId: string;
  dataRoomId: string;
  member: Partial<DataRoomMember>;
  isDataRoomMember?: boolean;
  onRevokeAccessSuccess?: () => void;
};

const UserWithAccess: React.FC<UserWithAccessProps> = (props) => {
  const {
    className,
    nodeId,
    dataRoomId,
    isDataRoomMember = true,
    member: {
      _id: memberId,
      fullName,
      email,
      canRevoke,
      lastAccessNodeAt,
      hasAccessToOwnFiles,
    },
    onRevokeAccessSuccess,
  } = props;

  const [isRevokedAccess, setIsRevokedAccess] = useState(false);

  const classes = classNames('user-with-access', className);

  const canOnlyAccessOwnFiles = hasAccessToOwnFiles && isRevokedAccess;

  const { t } = useTranslation();

  const { isUndoAvailable, setUndoTimeout, clearUndoTimeout } =
    useUndoActions();

  const [onRevokeAccess, isRevokingAccess] = useCallbackAsync(async () => {
    if (isDataRoomMember) {
      await dataRoomApi.members.revokeAccessForNode(memberId, nodeId);
    } else {
      await dataRoomApi.members.revokeAccessForConsumer(memberId, dataRoomId);
    }
    setUndoTimeout(() => {
      onRevokeAccessSuccess();
      setIsRevokedAccess(true);
    });
  });

  const [onUndo, isUndoing] = useCallbackAsync(async () => {
    clearUndoTimeout();

    if (isDataRoomMember) {
      await dataRoomApi.members.undoRevokeAccessForNode(memberId, nodeId);
    } else {
      await dataRoomApi.members.undoRevokeAccessForConsumer(
        memberId,
        dataRoomId,
      );
    }
  });

  return (
    <div className={classes}>
      <div>
        <p className={classNames({ 'not-viewed': !lastAccessNodeAt })}>
          {fullName || email || t('unknownUser')}
        </p>
        {lastAccessNodeAt && (
          <p className="last-access-node-at">
            {t('viewedOn', {
              date: dateFormat(
                new Date(lastAccessNodeAt),
                isDateFromLastDays(new Date(lastAccessNodeAt), 1)
                  ? 'hh:MM TT'
                  : 'dd mmm yyyy',
              ),
            })}
          </p>
        )}

        {canOnlyAccessOwnFiles && (
          <p className="own-files-only">
            {t('revokedAccessForFilesNotUploadedUser')}
          </p>
        )}
      </div>

      {canRevoke &&
        (isUndoAvailable ? (
          <UndoButton disabled={isUndoing} onUndo={onUndo} />
        ) : (
          <Button
            variant="link"
            icon={PauseIcon}
            disabled={isRevokingAccess}
            onClick={onRevokeAccess}
          >
            {t('revoke')}
          </Button>
        ))}
    </div>
  );
};

export default UserWithAccess;
